/**
 * Network error detection and retry logic
 * Provides utilities for handling network failures and implementing retry mechanisms
 */

import { errorLogger, logNetworkError } from './errorLogger'
import { notificationService } from '../services/NotificationService'
import { showSuccess, showWarning, showError, showConfirm } from './notiflix'

export interface RetryConfig {
  maxRetries: number
  baseDelay: number
  maxDelay: number
  backoffMultiplier: number
  retryCondition?: (error: Error) => boolean
}

export interface NetworkErrorInfo {
  isNetworkError: boolean
  isTimeout: boolean
  isServerError: boolean
  isClientError: boolean
  statusCode?: number
  retryable: boolean
}

const DEFAULT_RETRY_CONFIG: RetryConfig = {
  maxRetries: 3,
  baseDelay: 1000,
  maxDelay: 10000,
  backoffMultiplier: 2,
  retryCondition: (error) => isRetryableError(error)
}

/**
 * Analyze an error to determine if it's network-related and retryable
 */
export function analyzeNetworkError(error: Error): NetworkErrorInfo {
  const message = error.message.toLowerCase()
  const name = error.name.toLowerCase()
  
  // Check for network-related errors
  const isNetworkError = 
    message.includes('network') ||
    message.includes('fetch') ||
    message.includes('connection') ||
    message.includes('timeout') ||
    name.includes('networkerror') ||
    name.includes('typeerror') && message.includes('failed to fetch')

  // Check for timeout errors
  const isTimeout = 
    message.includes('timeout') ||
    message.includes('aborted') ||
    name.includes('timeouterror')

  // Extract status code if available
  let statusCode: number | undefined
  const statusMatch = message.match(/status:?\s*(\d{3})/i) || message.match(/(\d{3})\s*error/i)
  if (statusMatch) {
    statusCode = parseInt(statusMatch[1], 10)
  }

  // Determine error types based on status code
  const isServerError = statusCode ? statusCode >= 500 : false
  const isClientError = statusCode ? statusCode >= 400 && statusCode < 500 : false

  // Determine if error is retryable (avoid circular call)
  const retryable = isNetworkError || isTimeout || 
    (statusCode !== undefined && (statusCode >= 500 || [408, 429].includes(statusCode)))

  return {
    isNetworkError,
    isTimeout,
    isServerError,
    isClientError,
    statusCode,
    retryable
  }
}

/**
 * Check if an error is retryable
 */
export function isRetryableError(error: Error, statusCode?: number): boolean {
  const message = error.message.toLowerCase()
  const name = error.name.toLowerCase()
  
  // Always retry network errors and timeouts
  const isNetworkError = 
    message.includes('network') ||
    message.includes('fetch') ||
    message.includes('connection') ||
    message.includes('timeout') ||
    name.includes('networkerror') ||
    name.includes('typeerror') && message.includes('failed to fetch')

  const isTimeout = 
    message.includes('timeout') ||
    message.includes('aborted') ||
    name.includes('timeouterror')

  if (isNetworkError || isTimeout) {
    return true
  }

  // Use provided status code or try to extract it
  let finalStatusCode = statusCode
  if (!finalStatusCode) {
    const statusMatch = message.match(/status:?\s*(\d{3})/i) || message.match(/(\d{3})\s*error/i)
    if (statusMatch) {
      finalStatusCode = parseInt(statusMatch[1], 10)
    }
  }

  // Retry server errors (5xx)
  if (finalStatusCode && finalStatusCode >= 500) {
    return true
  }

  // Retry specific client errors
  if (finalStatusCode) {
    const retryableClientErrors = [408, 429] // Request Timeout, Too Many Requests
    return retryableClientErrors.includes(finalStatusCode)
  }

  return false
}

/**
 * Execute an operation with retry logic
 */
export async function withRetry<T>(
  operation: () => Promise<T>,
  config: Partial<RetryConfig> = {}
): Promise<T> {
  const finalConfig = { ...DEFAULT_RETRY_CONFIG, ...config }
  let lastError: Error
  
  for (let attempt = 1; attempt <= finalConfig.maxRetries + 1; attempt++) {
    try {
      return await operation()
    } catch (error) {
      lastError = error instanceof Error ? error : new Error(String(error))
      
      // Log the error
      logNetworkError(lastError, 'unknown', 'retry_operation')
      
      // Check if we should retry
      if (attempt > finalConfig.maxRetries || !finalConfig.retryCondition!(lastError)) {
        throw lastError
      }
      
      // Calculate delay with exponential backoff
      const delay = Math.min(
        finalConfig.baseDelay * Math.pow(finalConfig.backoffMultiplier, attempt - 1),
        finalConfig.maxDelay
      )
      
      // Add jitter to prevent thundering herd
      const jitteredDelay = delay + Math.random() * 1000
      
      console.log(`Retrying operation in ${Math.round(jitteredDelay)}ms (attempt ${attempt}/${finalConfig.maxRetries})`)
      
      await new Promise(resolve => setTimeout(resolve, jitteredDelay))
    }
  }
  
  throw lastError!
}

/**
 * Enhanced fetch with automatic retry and error handling
 */
export async function fetchWithRetry(
  url: string,
  options: RequestInit = {},
  retryConfig: Partial<RetryConfig> = {}
): Promise<Response> {
  const operation = async (): Promise<Response> => {
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), 30000) // 30 second timeout
    
    try {
      const response = await fetch(url, {
        ...options,
        signal: controller.signal
      })
      
      clearTimeout(timeoutId)
      
      // Check if response is ok
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`)
      }
      
      return response
    } catch (error) {
      clearTimeout(timeoutId)
      
      if (error instanceof Error) {
        // Enhance error with more context
        const enhancedError = new Error(`Fetch failed for ${url}: ${error.message}`)
        enhancedError.name = error.name
        enhancedError.stack = error.stack
        throw enhancedError
      }
      
      throw error
    }
  }
  
  return withRetry(operation, retryConfig)
}

/**
 * Network status monitoring
 */
class NetworkMonitor {
  private isOnline = navigator.onLine
  private listeners: ((online: boolean) => void)[] = []
  private reconnectAttempts = 0
  private maxReconnectAttempts = 5
  private reconnectDelay = 1000

  constructor() {
    this.setupEventListeners()
  }

  private setupEventListeners(): void {
    window.addEventListener('online', () => {
      this.isOnline = true
      this.reconnectAttempts = 0
      this.notifyListeners()
      showSuccess('เชื่อมต่อสำเร็จแล้ว')
    })

    window.addEventListener('offline', () => {
      this.isOnline = false
      this.notifyListeners()
      showWarning('การเชื่อมต่อขาดหาย บางฟีเจอร์อาจไม่ทำงาน')
    })
  }

  public getStatus(): boolean {
    return this.isOnline
  }

  public onStatusChange(callback: (online: boolean) => void): () => void {
    this.listeners.push(callback)
    
    // Return unsubscribe function
    return () => {
      this.listeners = this.listeners.filter(l => l !== callback)
    }
  }

  public async checkConnectivity(): Promise<boolean> {
    try {
      // Try to fetch a small resource to verify connectivity
      const response = await fetch('/favicon.ico', {
        method: 'HEAD',
        cache: 'no-cache'
      })
      
      const isConnected = response.ok
      
      if (isConnected !== this.isOnline) {
        this.isOnline = isConnected
        this.notifyListeners()
      }
      
      return isConnected
    } catch (error) {
      if (this.isOnline) {
        this.isOnline = false
        this.notifyListeners()
      }
      return false
    }
  }

  public async attemptReconnect(): Promise<boolean> {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      showError('ไม่สามารถเชื่อมต่อได้ กรุณาตรวจสอบอินเทอร์เน็ต')
      return false
    }

    this.reconnectAttempts++
    
    try {
      const isConnected = await this.checkConnectivity()
      
      if (isConnected) {
        this.reconnectAttempts = 0
        return true
      }
      
      // Schedule next reconnect attempt
      setTimeout(() => {
        this.attemptReconnect()
      }, this.reconnectDelay * this.reconnectAttempts)
      
      return false
    } catch (error) {
      errorLogger.logError(error instanceof Error ? error : new Error(String(error)), {
        type: 'reconnect_attempt',
        attempt: this.reconnectAttempts
      })
      
      return false
    }
  }

  private notifyListeners(): void {
    this.listeners.forEach(callback => {
      try {
        callback(this.isOnline)
      } catch (error) {
        console.error('Error in network status listener:', error)
      }
    })
  }
}

// Export singleton instance
export const networkMonitor = new NetworkMonitor()

/**
 * Wrapper for API calls with network error handling
 */
export async function apiCall<T>(
  operation: () => Promise<T>,
  operationName: string,
  showNotifications = true
): Promise<T> {
  try {
    // Check network status before making the call
    if (!networkMonitor.getStatus()) {
      throw new Error('No internet connection available')
    }

    return await withRetry(operation, {
      maxRetries: 2,
      retryCondition: (error) => {
        const errorInfo = analyzeNetworkError(error)
        return errorInfo.retryable
      }
    })
  } catch (error) {
    const apiError = error instanceof Error ? error : new Error(String(error))
    const errorInfo = analyzeNetworkError(apiError)
    
    // Log the error
    logNetworkError(apiError, operationName, 'api_call')
    
    // Show appropriate notification
    if (showNotifications) {
      if (errorInfo.isNetworkError) {
        const shouldRetry = await showConfirm(
          'เกิดข้อผิดพลาดเครือข่าย',
          `เกิดข้อผิดพลาดเครือข่ายระหว่าง ${operationName} ต้องการลองใหม่หรือไม่?`,
          'ลองใหม่',
          'ยกเลิก'
        )
        if (shouldRetry) {
          return apiCall(operation, operationName, false)
        }
      } else if (errorInfo.isServerError) {
        showError(`เซิร์ฟเวอร์ผิดพลาดระหว่าง ${operationName} กรุณาลองใหม่ภายหลัง`)
      } else {
        showError(`เกิดข้อผิดพลาดระหว่าง ${operationName}: ${apiError.message}`)
      }
    }
    
    throw apiError
  }
}